import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useBoard } from '@/hooks/useBoard';
import { useI18n } from '@/hooks/useI18n';
import { useToast } from '@/hooks/useToast';
import { ApiError } from '@/lib/api/client';
import { classroomApi } from '@/lib/api/endpoints';
import type { LessonSlot, TodayPayload } from '@/lib/api/types';
import { routes } from '@/lib/routes';

const formatTime = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/** B01-S11 — today's schedule for CLASSROOM_ONLY board session. */
const ClassroomTodayPage = () => {
  const { t } = useI18n();
  const { notify } = useToast();
  const navigate = useNavigate();
  const { board, isActive, close } = useBoard();

  const [today, setToday] = useState<TodayPayload | null>(null);
  const [busySlot, setBusySlot] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isActive) return;
    let cancelled = false;
    const load = async () => {
      try {
        const payload = await classroomApi.today();
        if (!cancelled) setToday(payload);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t('common.error'));
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [isActive, t]);

  if (!isActive || !board) {
    return <Navigate to={routes.classroomEntry} replace />;
  }

  const startLesson = async (slot: LessonSlot) => {
    setBusySlot(slot.slot_id);
    setError(null);
    try {
      const session = await classroomApi.startSession({ slot_id: slot.slot_id });
      notify(t('auth.classroom.started', { title: slot.course_title }), 'success');
      navigate(`${routes.classroomSession}?id=${session.session_id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setBusySlot(null);
    }
  };

  const handleClose = async () => {
    await close();
    navigate(routes.login);
  };

  const slots = today?.slots ?? [];
  const fallback = today?.fallback_courses ?? [];

  return (
    <div className="classroom-shell">
      <header className="classroom-shell-header">
        <div>
          <p className="classroom-shell-eyebrow">{t('auth.classroom.title')}</p>
          <h1>{t('auth.classroom.todayTitle')}</h1>
          <p>
            {board.teacher_display_name}
            {board.room ? ` · ${board.room}` : ''}
          </p>
        </div>
        <button type="button" className="auth-secondary" onClick={() => void handleClose()}>
          {t('auth.classroom.close')}
        </button>
      </header>

      {error && <p className="field-error">{error}</p>}

      {!today && !error && <p>{t('common.loading')}</p>}

      {today && slots.length === 0 && fallback.length === 0 && (
        <p className="auth-note">{t('auth.classroom.emptyToday')}</p>
      )}

      <div className="classroom-slot-list">
        {slots.map((slot) => (
          <article key={slot.slot_id} className="classroom-slot-card">
            <div>
              <strong>{slot.course_title}</strong>
              <p>
                {formatTime(slot.starts_at)}–{formatTime(slot.ends_at)} · {slot.room} ·{' '}
                {slot.lesson_type} · {slot.group_codes.join(', ')}
              </p>
            </div>
            <button
              type="button"
              className="auth-primary"
              disabled={busySlot === slot.slot_id}
              onClick={() => void startLesson(slot)}
            >
              {busySlot === slot.slot_id ? t('common.loading') : t('auth.classroom.startLesson')}
            </button>
          </article>
        ))}
      </div>
    </div>
  );
};

export default ClassroomTodayPage;
